/* eslint-disable react/prop-types */
import React from 'react';
import useSWR, { cache } from 'swr';
import Page from '@glowforge/ui-common/components/contentful-views/page';
import {
  getAllVideoTags,
  getVideoGalleryPage,
  getVideosByPageAndTags,
} from '../../api/contentful-videos';
import CustomPageMetadata from '../../components/custom-page-metadata';
import VideoGallery from '../../components/video/video-gallery';

/**
 * @typedef {object} InitialData
 * @property {import('contentful').Entry<any>} page
 * @property {import('../../api/contentful-videos').VideoEntryCollection} videos
 * @property {string[]} tags
 */

/**
 * Get a cache key for use with swr for this page
 * @param {object} query URL path and query params
 */
const getCacheKey = (query) => {
  const { gallery = '', tags = '' } = query;
  return `video-gallery-page-${gallery}-${tags}`;
};

/**
 * Load data required for page
 * @param {object} query URL path and query params
 */
const fetchData = async (query) => {
  const { gallery = '', tags: selectedTags = '' } = query;
  const page = await getVideoGalleryPage(gallery);

  const [
    videos,
    tags,
  ] = await Promise.all([
    getVideosByPageAndTags(page?.sys?.id, selectedTags),
    getAllVideoTags(page?.sys?.id),
  ]);

  return {
    page,
    videos,
    tags,
  };
};

/**
 * Render a page containing a gallery of video thumbnails
 * @param {object} props
 * @param {InitialData} props.initialData
 * @param {{[key: string]: string}} props.query
 */
const VideoGalleryPage = ({
  initialData,
  query,
}) => {
  const data = useSWR(
    getCacheKey(query),
    () => fetchData(query),
    { initialData, revalidateOnMount: true },
  );

  const {
    page,
    videos,
    tags,
  } = data.data;

  const {
    gallery,
    tags: selectedTagsParam,
    layout = 'grid',
  } = query;

  const selectedTags = selectedTagsParam ? selectedTagsParam.split(',') : [];

  const customSections = {
    'video-gallery': {
      component: VideoGallery,
      props: {
        gallery,
        videos,
        selectedTags,
        tags,
        layout,
      },
    },
  };

  return (
    <>
      <CustomPageMetadata page={page} />
      <Page
        className='VideoGalleryPage'
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...page?.fields}
        title={null}
        customSections={customSections} />
    </>
  );
};

/**
 * Get props required for gallery page
 * @param {import('next').NextPageContext} context
 */
VideoGalleryPage.getInitialProps = async (context) => {
  const { query } = context;
  const initialData = cache.get(getCacheKey(query)) || await fetchData(query);

  return {
    initialData,
    query,
  };
};

export default VideoGalleryPage;
